/**
 * ``useSnapshot`` — TanStack Query hook around
 * ``GET /api/query/snapshot``.
 *
 * Stream A PR 2 / commit M2 — first server-state hook on the map
 * surface (REQ-UI-026: server state via TanStack Query). Returns
 * the latest Producer positions within a viewport at-or-after a
 * ``since`` timestamp; downstream :class:`SnapshotLayer` populates
 * a MapLibre GeoJSON source from the result.
 *
 * # Cancellation
 *
 * TanStack Query cancels the underlying fetch when the component
 * unmounts (via the ``AbortSignal`` plumbed through ``apiFetch``).
 * Tested in :file:`use-snapshot.test.tsx`. M3+ SSE consumes the
 * snapshot result and switches over to live updates; cancellation
 * matters because the operator may navigate away mid-query.
 *
 * # Role gating
 *
 * The BFF gates ``/api/query/snapshot`` on operator+ (every read
 * role per REQ-AUTH-024..027). The UI mirrors with a soft assertion
 * — the hook is only enabled when the session is loaded and the
 * user has at least one of the map-reader roles. A user without
 * those roles would not reach the map view (the route guard
 * filters first), so the assertion is a defense-in-depth check
 * rather than the primary RBAC enforcement.
 *
 * # Query key
 *
 * Keyed on ``viewport + since``. Per Open Decision 2 in the M2
 * dispatch (in the plan), the consumer is responsible for
 * debouncing rapid viewport changes; the hook itself does not
 * debounce — :func:`installViewportSync` does (300 ms after the
 * settled ``moveend``), so a camera animation yields one viewport
 * at its end, not one per frame.
 *
 * The viewport enters the key and the URL through
 * :func:`quantizeViewport` (outward snap to the 1e-4° grid; the
 * same reader the Map and the two track feeds use). A camera that
 * settles on the *same* view — the first-load home ``flyTo``, an
 * inertia settle — reads back bounds that differ only in the last
 * digits; keyed raw, that re-fired the snapshot (``last()`` over
 * every hot-tier row, ~250 ms on the dev host) for a picture the SSE
 * stream had already kept current (ADR-016 amendment 2026-10-01).
 *
 * # Stale time + refetch policy
 *
 * 5 seconds. Map pans within 5 seconds re-use the cached snapshot
 * for the same viewport+since key. M3 SSE makes this moot for live
 * updates; M2 ships a conservative number so the operator-facing
 * "is the data fresh" question has a known answer.
 * ``refetchOnWindowFocus`` is off: the SSE stream is the freshness
 * mechanism (REQ-UI-004..009), and on a stream recovery
 * :func:`useSse` invalidates this key explicitly (REQ-UI-009) — the
 * one resync the design calls for. Mount / reconnect keep TanStack's
 * defaults (rationale in ``use-tracks-feed.ts`` §"Refetch policy").
 *
 * # Placeholder data (keepPreviousData)
 *
 * Because the query key includes the viewport, a pan/zoom re-keys the
 * query and — by default — ``data`` would flash to ``undefined``
 * while the new viewport's snapshot is in flight. The Map passes
 * ``snapshot={query.data ?? null}`` to the layer components, so that
 * flash briefly nulls the ``snapshot`` prop on every viewport change.
 * Setting ``placeholderData: keepPreviousData`` keeps the previous
 * viewport's snapshot in place during the refetch — ``data`` stays
 * non-null and ``isPlaceholderData`` flags the transitional state.
 * This removes the null transition at the source (follow-up to the
 * cluster-layer detach/re-attach flicker fixed structurally in
 * :file:`cluster-layer.tsx`).
 *
 * Cross-references:
 *   - REQ-UI-003 (snapshot request on map open).
 *   - REQ-UI-026 (TanStack Query for server state).
 *   - REQ-AUTH-022 (UI mirrors BFF RBAC; BFF is the source of truth).
 *   - REQ-AUTH-024..027 (role-capability matrix).
 *   - ADR-016 §3 (snapshot orchestration).
 *   - packages/meridian_bff/src/meridian_bff/_query_routes.py
 *     §snapshot_route.
 */
import { useQuery, keepPreviousData, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { SnapshotResponse, SnapshotViewport } from "@/map/snapshot-types";
import { isSnapshotResponse } from "@/map/snapshot-types";
import { quantizeViewport } from "@/map/viewport-sync";
import type { Role } from "@/auth/session-types";

/**
 * Map-reader roles per REQ-AUTH-024..027. The BFF's snapshot route
 * requires ``Depends(require_roles("operator", "analyst", "admin",
 * "support_engineer"))``; this constant mirrors the set.
 */
export const MAP_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "admin",
  "support_engineer",
];

/**
 * Default :class:`useQuery` stale time, in milliseconds. See module
 * docstring §"Stale time".
 */
export const SNAPSHOT_STALE_TIME_MS = 5_000;

/**
 * Compose the URL the BFF expects.
 *
 * Exported for unit-test reach into the query-string composition
 * without spinning up the full hook + msw server.
 */
export function composeSnapshotUrl(viewport: SnapshotViewport, since: Date): string {
  const bbox = quantizeViewport(viewport);
  const params = new URLSearchParams({
    min_longitude: bbox.min_longitude.toString(),
    min_latitude: bbox.min_latitude.toString(),
    max_longitude: bbox.max_longitude.toString(),
    max_latitude: bbox.max_latitude.toString(),
    since: since.toISOString(),
  });
  return `/api/query/snapshot?${params.toString()}`;
}

/**
 * The query-key shape — exported so consumers
 * (:func:`useQueryClient().invalidateQueries`) can target it. Carries
 * the :func:`quantizeViewport`-snapped bounds, never the raw floats
 * (module docstring §"Query key").
 *
 * Cross-references:
 *   - REQ-UI-026.
 */
export function snapshotQueryKey(viewport: SnapshotViewport, since: Date): readonly unknown[] {
  const bbox = quantizeViewport(viewport);
  return [
    "snapshot",
    bbox.min_longitude,
    bbox.min_latitude,
    bbox.max_longitude,
    bbox.max_latitude,
    since.toISOString(),
  ];
}

/**
 * Options for :func:`useSnapshot`.
 */
export interface UseSnapshotOptions {
  /** Bounding box to query. ``null`` disables the query — used to
   *  gate on map readiness (the bounds aren't known until the map's
   *  first ``load`` event). */
  viewport: SnapshotViewport | null;
  /**
   * Snapshot ``since`` timestamp. ``null`` disables the query.
   *
   * For the map-view initial load, ``since`` is "now" at first map
   * mount — the BFF returns the latest per-Producer record at-or-
   * after that point. The Map component computes a stable
   * ``Date.now()`` at mount and passes it; otherwise every render
   * would change the cache key and invalidate the snapshot.
   */
  since: Date | null;
  /** Override the default stale time. Tests set 0; consumers
   *  generally leave this. */
  staleTimeMs?: number;
}

/**
 * The :func:`useSnapshot` result. ``UseQueryResult`` carries
 * ``data | error | isPending | isError | isSuccess | refetch`` and
 * the rest of the TanStack surface; the hook does not narrow it.
 */
export type UseSnapshotResult = UseQueryResult<SnapshotResponse, ApiError>;

/**
 * Issue a snapshot request and surface the result as a
 * :class:`UseQueryResult`.
 *
 * @param options - Viewport, ``since`` timestamp, optional stale
 *     time override.
 * @returns TanStack Query result. The query is disabled until the
 *     viewport and ``since`` are non-null and the session has a
 *     map-reader role.
 *
 * Cross-references:
 *   - REQ-UI-003 / REQ-UI-026.
 *   - REQ-AUTH-024..027.
 */
export function useSnapshot(options: UseSnapshotOptions): UseSnapshotResult {
  const session = useSession();
  const hasReadRole = session.hasAnyRole(MAP_READER_ROLES);
  const enabled = options.viewport !== null && options.since !== null && hasReadRole;

  return useQuery<SnapshotResponse, ApiError>({
    queryKey:
      options.viewport && options.since
        ? snapshotQueryKey(options.viewport, options.since)
        : ["snapshot", "disabled"],
    queryFn: async ({ signal }) => {
      if (options.viewport === null || options.since === null) {
        // ``enabled: false`` should prevent this branch, but the
        // type system requires queryFn to be unconditional. The
        // throw is intentional — operator-visibility per CLAUDE.md
        // §8.1.
        throw new ApiError(0, null, "useSnapshot: query ran without viewport/since");
      }
      const url = composeSnapshotUrl(options.viewport, options.since);
      const raw = await apiFetch<unknown>(url, { signal });
      if (!isSnapshotResponse(raw)) {
        // Boundary-validation failure. The BFF surface shifted —
        // halt with a structured error so the api-change-request
        // workflow can run. Per CLAUDE.md §8.1 (silent failures
        // forbidden), surface loudly.
        console.error("[meridian/map/use-snapshot] snapshot response failed shape validation", raw);
        throw new ApiError(500, raw, "snapshot response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    staleTime: options.staleTimeMs ?? SNAPSHOT_STALE_TIME_MS,
    // Keep the previous viewport's snapshot as placeholder data while
    // the new viewport (cache key) fetches, so ``data`` never flashes
    // to ``undefined`` on pan/zoom — the Map's ``snapshot`` prop stays
    // non-null across the refetch. See the module docstring
    // §"Placeholder data" for the cluster-flicker rationale.
    placeholderData: keepPreviousData,
    // The SSE stream carries the live picture and ``useSse`` invalidates
    // this key on a recovery (REQ-UI-009); a window-focus refetch of the
    // ``last()``-over-every-row query adds nothing. Module docstring
    // §"Stale time + refetch policy".
    refetchOnWindowFocus: false,
    // Retry transient errors twice; never retry on auth failures
    // (the global QueryClient in main.tsx already filters 401/403).
    // Local override here keeps the snapshot hook self-contained
    // for tests that construct an isolated QueryClient.
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
